import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Download, Filter, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const Reports = () => {
  const { user } = useContext(AuthContext);
  const [reportType, setReportType] = useState('daily-reports');
  const [data, setData] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    machinery_id: '',
    operator_id: '',
    status: ''
  });

  useEffect(() => {
    fetchMachinery();
    if (user.role !== 'operator') {
      fetchOperators();
    }
  }, []);

  const fetchMachinery = async () => {
    try {
      const response = await axios.get(`${API}/machinery`);
      setMachinery(response.data);
    } catch (error) {
      toast.error('Error al cargar maquinaria');
    }
  };

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/operators`);
      setOperators(response.data);
    } catch (error) {
      toast.error('Error al cargar operadores');
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      const params = new URLSearchParams();
      
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.machinery_id) params.append('machinery_id', filters.machinery_id);
      
      if (reportType === 'daily-reports') {
        endpoint = '/reports/daily-reports-data';
        if (filters.operator_id && user.role !== 'operator') {
          params.append('operator_id', filters.operator_id);
        }
      } else if (reportType === 'maintenance') {
        endpoint = '/reports/maintenance-data';
        if (filters.status) params.append('status', filters.status);
      }
      
      const response = await axios.get(`${API}${endpoint}?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar datos del reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add logo
    const logoImg = new Image();
    logoImg.src = '/logo-mndp.jpg';
    doc.addImage(logoImg, 'JPEG', 14, 10, 30, 15);
    
    doc.setFontSize(18);
    doc.text('MNDP MaqControl - Reporte', 50, 18);
    doc.setFontSize(11);
    doc.text(`Tipo: ${reportType === 'daily-reports' ? 'Partes Diarios' : 'Mantenimientos'}`, 14, 32);
    doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 38);
    doc.setFontSize(9);
    doc.text('MNDP Asociados - Ingeniería y Construcción', 14, 44);
    
    if (reportType === 'daily-reports') {
      const tableData = data.map(item => [
        item.date,
        item.machinery_name,
        item.operator_name,
        item.hours_worked.toFixed(1),
        item.diesel_consumed,
        item.location
      ]);
      
      doc.autoTable({
        head: [['Fecha', 'Maquinaria', 'Operador', 'Horas', 'Diésel (L)', 'Ubicación']],
        body: tableData,
        startY: 50,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] }
      });
    } else if (reportType === 'maintenance') {
      const tableData = data.map(item => [
        item.scheduled_date,
        item.machinery_name,
        item.type,
        item.description,
        `Bs. ${item.cost.toFixed(2)}`,
        item.status === 'pending' ? 'Pendiente' : 'Completado'
      ]);
      
      doc.autoTable({
        head: [['Fecha', 'Maquinaria', 'Tipo', 'Descripción', 'Costo', 'Estado']],
        body: tableData,
        startY: 50,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [249, 115, 22] }
      });
    }
    
    doc.save(`reporte-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Reporte PDF descargado');
  };

  const exportToExcel = () => {
    let worksheetData = [];
    
    if (reportType === 'daily-reports') {
      worksheetData = data.map(item => ({
        'Fecha': item.date,
        'Maquinaria': item.machinery_name,
        'Operador': item.operator_name,
        'Horas Trabajadas': item.hours_worked,
        'Diésel (L)': item.diesel_consumed,
        'Aceite (L)': item.oil_consumed,
        'Ubicación': item.location,
        'Descripción': item.work_description
      }));
    } else if (reportType === 'maintenance') {
      worksheetData = data.map(item => ({
        'Fecha Programada': item.scheduled_date,
        'Maquinaria': item.machinery_name,
        'Tipo': item.type,
        'Descripción': item.description,
        'Costo': item.cost,
        'Estado': item.status === 'pending' ? 'Pendiente' : 'Completado',
        'Fecha Completado': item.completed_date || 'N/A'
      }));
    }
    
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reporte');
    
    XLSX.writeFile(workbook, `reporte-${reportType}-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Reporte Excel descargado');
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Reportes
        </h1>
        <p className="text-slate-600 mt-2">Exportación de datos e informes</p>
      </div>

      {/* Report Type Selection */}
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Tipo de Reporte
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            data-testid="report-type-daily"
            onClick={() => setReportType('daily-reports')}
            className={`p-4 border-2 rounded-md transition-all ${
              reportType === 'daily-reports'
                ? 'border-orange-500 bg-orange-50'
                : 'border-slate-200 hover:border-orange-300'
            }`}
          >
            <FileText className="mb-2 text-orange-500" size={24} />
            <h4 className="font-semibold">Partes Diarios</h4>
            <p className="text-sm text-slate-600">Actividades y consumos</p>
          </button>
          <button
            data-testid="report-type-maintenance"
            onClick={() => setReportType('maintenance')}
            className={`p-4 border-2 rounded-md transition-all ${
              reportType === 'maintenance'
                ? 'border-orange-500 bg-orange-50'
                : 'border-slate-200 hover:border-orange-300'
            }`}
          >
            <FileText className="mb-2 text-orange-500" size={24} />
            <h4 className="font-semibold">Mantenimientos</h4>
            <p className="text-sm text-slate-600">Historial de mantenimiento</p>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <Filter size={20} className="mr-2" />
          Filtros
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Fecha Inicio</label>
            <input
              type="date"
              data-testid="start-date-filter"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Fecha Fin</label>
            <input
              type="date"
              data-testid="end-date-filter"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Maquinaria</label>
            <select
              data-testid="machinery-filter"
              value={filters.machinery_id}
              onChange={(e) => setFilters({ ...filters, machinery_id: e.target.value })}
              className="input-field"
            >
              <option value="">Todas</option>
              {machinery.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          {reportType === 'daily-reports' && user.role !== 'operator' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Operador</label>
              <select
                data-testid="operator-filter"
                value={filters.operator_id}
                onChange={(e) => setFilters({ ...filters, operator_id: e.target.value })}
                className="input-field"
              >
                <option value="">Todos</option>
                {operators.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          )}
          {reportType === 'maintenance' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
              <select
                data-testid="status-filter"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="completed">Completado</option>
              </select>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Button
            data-testid="generate-report-button"
            onClick={fetchReportData}
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Generando...' : 'Generar Reporte'}
          </Button>
        </div>
      </div>

      {/* Results */}
      {data.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Resultados ({data.length} registros)
            </h3>
            <div className="flex space-x-3">
              <Button
                data-testid="export-pdf-button"
                onClick={exportToPDF}
                className="btn-secondary flex items-center"
              >
                <Download size={18} className="mr-2" />
                Exportar PDF
              </Button>
              <Button
                data-testid="export-excel-button"
                onClick={exportToExcel}
                className="btn-primary flex items-center"
              >
                <Download size={18} className="mr-2" />
                Exportar Excel
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {reportType === 'daily-reports' ? (
                    <>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Maquinaria</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Operador</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Horas</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Diésel</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Ubicación</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Maquinaria</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Tipo</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Costo</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Estado</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                    {reportType === 'daily-reports' ? (
                      <>
                        <td className="p-3 text-sm">{new Date(item.date).toLocaleDateString()}</td>
                        <td className="p-3 text-sm font-medium">{item.machinery_name}</td>
                        <td className="p-3 text-sm">{item.operator_name}</td>
                        <td className="p-3 text-sm mono">{item.hours_worked.toFixed(1)}</td>
                        <td className="p-3 text-sm mono">{item.diesel_consumed} L</td>
                        <td className="p-3 text-sm">{item.location}</td>
                      </>
                    ) : (
                      <>
                        <td className="p-3 text-sm">{new Date(item.scheduled_date).toLocaleDateString()}</td>
                        <td className="p-3 text-sm font-medium">{item.machinery_name}</td>
                        <td className="p-3 text-sm">{item.type}</td>
                        <td className="p-3 text-sm mono">Bs. {item.cost.toFixed(2)}</td>
                        <td className="p-3 text-sm">
                          <span className={`status-badge status-${item.status}`}>
                            {item.status === 'pending' ? 'Pendiente' : 'Completado'}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && data.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
          <FileText size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600">Selecciona filtros y genera un reporte para ver resultados</p>
        </div>
      )}
    </div>
  );
};

export default Reports;
